// Shared plumbing for `grid vault …` (#1766).
//
// Vault items are per-grid (decision 023). The v2 vault router
// (packages/api/src/routes/v2/vault.ts) is keyed on the grid's UUID
// (`:grid_id` → `getGridById(grids/<id>)`), NOT its slug — so the CLI must
// resolve the active/`--grid` slug to its grid_id before composing a path.
//
// The secret value is write-only everywhere in this group: it is read from a
// hidden prompt or `--value-stdin`, never from argv, and never printed back.

import { createInterface } from 'node:readline';
import type { IV2Client } from '../../api/v2-client.js';
import { getActiveOrg } from '../../active-org.js';
import { V2ApiError } from '../../api/v2-errors.js';

/**
 * The wire shape of a vault item as returned by GET
 * `/api/v2/grids/:grid_id/vault/items`. Deliberately has no `value` field —
 * item values are never returned by the list endpoint.
 */
export interface VaultItemWire {
  item_id: string;
  key: string;
  type: string;
  scope: string;
  last_resolved_at: string | null;
  last_resolved_by: string | null;
  resolve_count: number;
  grants_count?: number;
  demo_value: string | null;
  description: string | null;
}

export interface ResolvedGrid {
  gridId: string;
  slug: string;
  name: string;
}

/**
 * Resolve the grid this command targets: the `--grid <slug>` override when
 * given, else the active grid. Returns the grid_id (UUID for the path) plus
 * the slug/name for the target notice.
 */
export async function resolveVaultGrid(
  client: IV2Client,
  gridOverride: string | undefined,
): Promise<ResolvedGrid> {
  const active = await getActiveOrg(client, { override: gridOverride });
  return { gridId: active.org_id, slug: active.slug, name: active.name };
}

/**
 * Print which grid the command is about to write to / read from. Goes to
 * stderr so it is always visible on a terminal but never pollutes `--json`
 * stdout. This is the footgun guard: the Console incident was a write to the
 * wrong grid because the target was never surfaced.
 */
export function announceGridTarget(grid: ResolvedGrid): void {
  process.stderr.write(`Targeting grid ${grid.slug} (${grid.name}).\n`);
}

/** Compose the vault items collection path for a grid. */
export function vaultItemsPath(gridId: string): string {
  return `/api/v2/grids/${encodeURIComponent(gridId)}/vault/items`;
}

/** Compose the vault single-item path for a grid + item. */
export function vaultItemPath(gridId: string, itemId: string): string {
  return `${vaultItemsPath(gridId)}/${encodeURIComponent(itemId)}`;
}

/**
 * Find an existing vault item by key within a scope. Returns null when no
 * item with that key exists in the scope. Used by `set` (upsert) and `rotate`
 * (must-exist).
 */
export async function findVaultItemByKey(
  client: IV2Client,
  gridId: string,
  key: string,
  scope: string,
): Promise<VaultItemWire | null> {
  const resp = await client.get<{ items?: VaultItemWire[] }>(
    vaultItemsPath(gridId),
    { scope },
  );
  const items = Array.isArray(resp?.items) ? resp.items : [];
  return items.find((it) => it.key === key && it.scope === scope) ?? null;
}

export interface ReadSecretValueOpts {
  /** When true, read the value from stdin instead of prompting. */
  valueStdin?: boolean;
  /**
   * Injected reader for the hidden prompt. Defaults to a muted-readline
   * terminal prompt. Tests inject a stub so they never touch a TTY.
   */
  readValue?: () => Promise<string>;
  /** Injected stdin reader (tests override). Defaults to draining process.stdin. */
  readStdin?: () => Promise<string>;
}

/**
 * Obtain the secret value out-of-band: from `--value-stdin` (full stdin, one
 * trailing newline stripped) or a hidden prompt. The value is never echoed
 * and never read from argv.
 */
export async function readSecretValue(opts: ReadSecretValueOpts): Promise<string> {
  if (opts.valueStdin) {
    const raw = await (opts.readStdin ?? drainStdin)();
    const value = raw.replace(/\r?\n$/, '');
    if (value.length === 0) {
      // A dead upstream producer (`failing-cmd | grid vault rotate KEY
      // --value-stdin`) must not silently blank the secret.
      throw new V2ApiError(400, 'VALIDATION_FAILED', 'No value provided on stdin.');
    }
    return value;
  }
  const value = await (opts.readValue ?? promptHiddenValue)();
  if (value.length === 0) {
    throw new V2ApiError(400, 'VALIDATION_FAILED', 'No value provided.');
  }
  return value;
}

/** Read the whole of stdin as a UTF-8 string. */
async function drainStdin(): Promise<string> {
  const chunks: Buffer[] = [];
  for await (const chunk of process.stdin) {
    chunks.push(chunk as Buffer);
  }
  return Buffer.concat(chunks).toString('utf8');
}

/**
 * Prompt for a secret on the terminal with the typed characters masked. Uses
 * the well-known muted-readline trick: `_writeToOutput` emits the prompt once
 * and swallows every subsequent write (the echoed keystrokes). Only used from
 * the interactive path; unit tests inject `readValue` instead.
 */
function promptHiddenValue(): Promise<string> {
  const promptText = 'Value: ';
  return new Promise((resolve) => {
    const rl = createInterface({ input: process.stdin, output: process.stderr, terminal: true });
    let shown = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (rl as any)._writeToOutput = (str: string) => {
      if (!shown) {
        process.stderr.write(promptText);
        shown = true;
      } else if (str.includes('\n')) {
        process.stderr.write('\n');
      }
      // swallow the echoed keystrokes
    };
    rl.question(promptText, (answer) => {
      rl.close();
      resolve(answer);
    });
  });
}
